import { useState } from "react";
import { type StandingTeam } from "@/api/tournamentAnalysis";
import { Loader } from "../common/loader";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/shadcn/dialog";
import dayjs from "dayjs";
import { dateFormat } from "@/lib/utils";
import { PlayoffBracket } from "./PlayoffBracket";

type StandingsTab = "overall" | "home" | "away" | "playoffs";

interface TournamentStandingsTableProps {
    overallStandings?: StandingTeam[];
    homeStandings?: StandingTeam[];
    awayStandings?: StandingTeam[];
    isLoading?: boolean;
    activeTab: StandingsTab;
}

export function TournamentStandingsTable({
    overallStandings,
    homeStandings,
    awayStandings,
    isLoading,
    activeTab,
}: TournamentStandingsTableProps) {
    const standingsByTab = { overall: overallStandings, home: homeStandings, away: awayStandings };
    const standings =
        activeTab === "playoffs" ? overallStandings : standingsByTab[activeTab as "overall" | "home" | "away"];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader message="Loading standings..." />
            </div>
        );
    }

    if (!standings || standings.length === 0) {
        return (
            <div>
                <div className="text-center text-on-surface-variant py-8 text-sm">No data available</div>
            </div>
        );
    }

    return (
        <div>
            {activeTab === "playoffs" ? (
                <PlayoffBracket standings={standings} />
            ) : (
                <>
                    <div className="md:hidden">
                        <StandingsMobileCards standings={[...standings].sort((a, b) => a.position - b.position)} />
                    </div>
                    <div className="hidden md:block">
                        <StandingsTable standings={[...standings].sort((a, b) => a.position - b.position)} />
                    </div>
                </>
            )}
        </div>
    );
}

interface StandingsTableProps {
    standings: StandingTeam[];
}

function StandingsMobileCards({ standings }: StandingsTableProps) {
    const maxPoints = Math.max(...standings.map((t) => t.totalPoints), 1);
    void maxPoints; // retained for future use
    return (
        <div className="flex flex-col bg-surface-container-lowest">
            {standings.map((team, index) => {
                const isRelegationZone = index >= standings.length - 2;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                return (
                    <div
                        key={team.teamId}
                        className={`flex flex-col gap-1.5 px-3 py-2.5 border-b border-outline-variant/10 ${rowBg}`}
                        style={isRelegationZone ? { background: "rgba(220,38,38,0.03)" } : undefined}
                    >
                        <div className="flex items-stretch gap-3">
                            <span
                                className={`font-headline font-bold text-xl w-7 text-center shrink-0 tabular-nums flex items-center justify-center ${
                                    isRelegationZone ? "text-general-red" : "text-on-surface-variant"
                                }`}
                                style={{
                                    borderLeft: isRelegationZone
                                        ? "3px solid var(--color-general-red)"
                                        : "3px solid transparent",
                                }}
                            >
                                {team.position}
                            </span>
                            <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                                <span className="font-bold text-sm text-primary min-w-0 truncate">{team.teamName}</span>
                                <div className="flex items-center gap-x-3">
                                    <div className="flex flex-col items-center">
                                        <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                            MP
                                        </span>
                                        <span className="text-xs font-bold text-on-surface">{team.mp}</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                            W
                                        </span>
                                        <span className="text-xs font-bold text-general-green">{team.w}</span>
                                    </div>
                                    {team.wo > 0 && (
                                        <div className="flex flex-col items-center">
                                            <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                                WO
                                            </span>
                                            <span className="text-xs font-bold text-general-green/70">{team.wo}</span>
                                        </div>
                                    )}
                                    {team.lo > 0 && (
                                        <div className="flex flex-col items-center">
                                            <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                                LO
                                            </span>
                                            <span className="text-xs font-bold text-general-red/70">{team.lo}</span>
                                        </div>
                                    )}
                                    <div className="flex flex-col items-center">
                                        <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                            L
                                        </span>
                                        <span className="text-xs font-bold text-general-red">{team.l}</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                            G
                                        </span>
                                        <span className="text-xs text-on-surface tabular-nums">
                                            {team.goalsScored}:{team.goalsConceded}
                                        </span>
                                    </div>
                                </div>
                                <div className="[&>div]:justify-start">
                                    <FormIndicator recentForm={team.recentForm} />
                                </div>
                            </div>
                            <div className="flex flex-col items-center justify-center shrink-0">
                                <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold leading-none">
                                    PTS
                                </span>
                                <span className="text-xl font-bold text-general-blue tabular-nums leading-tight">
                                    {team.totalPoints}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function StandingsTable({ standings }: StandingsTableProps) {
    const headerCells = ["#", "Team", "MP", "W", "WO", "LO", "L", "G", "PTS", "Form"];

    return (
        <div className="overflow-auto bg-surface-container-lowest">
            <table className="w-full min-w-max border-collapse">
                <thead>
                    <tr>
                        {headerCells.map((label, i) => (
                            <th
                                key={label}
                                className="data-table-header px-4 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest whitespace-nowrap text-left"
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    borderLeft: i === 0 ? "3px solid var(--color-surface-container-high)" : undefined,
                                    zIndex: 20,
                                }}
                            >
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {standings.map((team, index) => {
                        const isRelegationZone = index >= standings.length - 2;
                        return (
                            <tr
                                key={team.teamId}
                                className="data-table-row transition-colors"
                                style={{
                                    background: isRelegationZone ? "rgba(220,38,38,0.03)" : undefined,
                                }}
                            >
                                <td
                                    className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap"
                                    style={{
                                        borderLeft: isRelegationZone
                                            ? "3px solid var(--color-general-red)"
                                            : "3px solid var(--color-surface)",
                                    }}
                                >
                                    {index + 1}
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-primary whitespace-nowrap">
                                    {team.teamName}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-on-surface whitespace-nowrap">
                                    {team.mp}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-general-green whitespace-nowrap">
                                    {team.w}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-general-green whitespace-nowrap">
                                    {team.wo}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-general-red whitespace-nowrap">
                                    {team.lo}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-general-red whitespace-nowrap">
                                    {team.l}
                                </td>
                                <td className="px-4 py-3 text-sm text-center text-on-surface whitespace-nowrap">
                                    {team.goalsScored} : {team.goalsConceded}
                                </td>
                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                    <PointsWithPenalties points={team.totalPoints} penalties={team.teamPenalties} />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <FormIndicator recentForm={team.recentForm} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

interface PointsWithPenaltiesProps {
    points: number;
    penalties?: Array<{
        penaltyId: number;
        penaltyPoints: number;
        reason: string;
    }>;
}

function PointsWithPenalties({ points, penalties }: PointsWithPenaltiesProps) {
    const hasPenalties = penalties && penalties.length > 0;

    if (!hasPenalties) {
        return <span className="font-bold text-general-blue">{points}</span>;
    }

    const totalPenaltyPoints = penalties.reduce((sum, penalty) => sum + penalty.penaltyPoints, 0);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button className="font-bold text-general-blue cursor-help">{points}</button>
            </TooltipTrigger>
            <TooltipContent>
                <div className="space-y-2">
                    <div className="font-semibold text-general-red">Penalties</div>
                    {penalties.map((penalty) => (
                        <div key={penalty.penaltyId} className="text-sm">
                            <div className="text-on-surface">{penalty.reason}</div>
                            <div className="text-general-red font-semibold">-{penalty.penaltyPoints} points</div>
                        </div>
                    ))}
                    <div className="border-t border-outline-variant/20 pt-1 mt-2">
                        <div className="text-xs text-on-surface-variant">
                            Total penalty: -{totalPenaltyPoints} points
                        </div>
                    </div>
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

interface FormIndicatorProps {
    recentForm?: Array<{
        matchId: number;
        opponentTeamId: number;
        opponentTeamName: string;
        result: string;
        score: string;
        date: string;
    }>;
}

function FormIndicator({ recentForm }: FormIndicatorProps) {
    const [dialogMatch, setDialogMatch] = useState<NonNullable<FormIndicatorProps["recentForm"]>[number] | null>(null);

    if (!recentForm || recentForm.length === 0) {
        return <div className="text-on-surface-variant text-xs text-center">-</div>;
    }

    const lastMatches = recentForm.slice(-5);

    const getResultColor = (result: string) => {
        const upperResult = result.toUpperCase();
        if (upperResult === "W") return "bg-general-green";
        if (upperResult === "WO") return "bg-general-green/50";
        if (upperResult === "WP") return "bg-general-green/20";
        if (upperResult === "L") return "bg-general-red";
        if (upperResult === "LO") return "bg-general-red/50";
        if (upperResult === "LP") return "bg-general-red/20";
        return "bg-on-surface-variant";
    };

    const getResultLabel = (result: string) => {
        const upperResult = result.toUpperCase();
        if (upperResult === "W") return "Win";
        if (upperResult === "WO") return "Win in Overtime";
        if (upperResult === "WP") return "Win in Penalty";
        if (upperResult === "L") return "Loss";
        if (upperResult === "LO") return "Loss in Overtime";
        if (upperResult === "LP") return "Loss in Penalty";
        return result;
    };

    return (
        <>
            <div className="flex items-center justify-center gap-1">
                {lastMatches.map((match) => (
                    <Tooltip key={match.matchId}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold cursor-pointer transition-transform hover:scale-110 ${getResultColor(match.result)}`}
                                onClick={() => setDialogMatch(match)}
                            >
                                {match.result.toUpperCase()}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <div className="space-y-1">
                                <div className="font-semibold">{getResultLabel(match.result)}</div>
                                <div className="text-on-surface">vs {match.opponentTeamName}</div>
                                <div className="text-on-surface">Score: {match.score}</div>
                                <div className="text-on-surface-variant text-xs">
                                    {dayjs(match.date).format(dateFormat)}
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                ))}
            </div>
            <Dialog open={!!dialogMatch} onOpenChange={(open) => !open && setDialogMatch(null)}>
                <DialogContent className="p-0 w-[calc(100vw-2rem)] max-w-sm bg-surface-container-low border border-outline-variant/20 rounded-xl shadow-2xl">
                    <DialogHeader className="px-5 py-4 border-b border-outline-variant/10">
                        <DialogTitle className="text-on-surface font-semibold text-base">Match Details</DialogTitle>
                    </DialogHeader>
                    {dialogMatch && (
                        <div className="px-5 py-4 flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                                <div
                                    className={`w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold ${getResultColor(dialogMatch.result)}`}
                                >
                                    {dialogMatch.result.toUpperCase()}
                                </div>
                                <span className="font-semibold text-on-surface">
                                    {getResultLabel(dialogMatch.result)}
                                </span>
                            </div>
                            <div className="text-sm text-on-surface">
                                vs <strong>{dialogMatch.opponentTeamName}</strong>
                            </div>
                            <div className="text-sm text-on-surface">
                                Score: <strong>{dialogMatch.score}</strong>
                            </div>
                            <div className="text-xs text-on-surface-variant">
                                {dayjs(dialogMatch.date).format(dateFormat)}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
